import {StandingWidgetTabViewModel} from "./standings-widget.tab-view-model";
import {StandingsWidgetGroupedRowsViewModel} from "./standings-widget.grouped-rows-view-model";
import {computed, observable, override} from "mobx";
import {StandingsWidgetRowViewModel} from "./standings-widget.row-view-model";
import {BaseModuleState} from "../../../infrastructure/models/base-module-state";
import {StandingsWidgetConfig} from "../standings-widget.config";
import {CompetitionFactory} from "../../../domain/common/competition/domain-services/competition-factory";
import {StandingsRowDomainModel} from "../../../domain/standings/models/row/standings.row-domain-model";
import {CompetitorViewModel} from "../../../domain/common/competitor/models/competitor-view-model";
import {AppStore} from "../../../core/app/app-store";
import {LoggerUtils} from "../../../infrastructure/utils/logger/logger-utils";
import {DatetimeUtils} from "../../../infrastructure/utils/datetime-utils";
import {DeviceViewModel} from "../../../core/models/device.view-model";
import {TermsUtils} from "../../../infrastructure/utils/terms-utils";
import {NavigationManager} from "../../../core/managers/navigation-manager";
import {BaseMobxState} from "../../../infrastructure/mobx/base-mobx-state";
import {StandingWidgetCompetitorViewModel} from "./standings-widget.competitor-view-model";
import {StandingsDestinationViewModel} from "../../../domain/standings/models/destinations/standings.destinations.view-model";
import {StandingsViewModel} from "../../../domain/standings/models/standings.view-model";
import {StandingsHeaderViewModel} from "../../../domain/standings/models/header/standings-header.view-model";
import {STANDINGS_PREVIEW_WIDGET} from "../../standings-preview-widget/standings-preview-widget.consts";
import {makeObservableClass} from "../../../infrastructure/decorators/observable";
import {APP, DBA, SPORT_TYPES} from "../../../core/app/consts";
import {CompetitionViewModel} from "../../../domain/common/competition/models/competition-view-model";
import {COMPONENT_STATES, FETCHING_STATES, ROUTE_NAMES} from "../../../core/consts";
import {StandingsWidgetTerms} from "../standings-widget.terms";
import {StandingsWidgetFilterTableTypesViewModel} from "./filters/standings-widget.filter.table-types.view-model";
import {StandingsWidgetFilterGroupCategoryViewModel} from "./filters/standings-widget.filter.group-category.view-model";
import {StandingsWidgetFilterViewModel} from "./filters/standings-widget.filter.view-model";
import {CompetitionDomainModel} from "../../../domain/common/competition/models/competition-domain-model";
import {StandingsWidgetStageViewModel} from "./standings-widget.stage.view-model";
import {SeasonStageDomainModel} from "../../../domain/season-stage/models/season-stage.domain-model";
import {BaseLinkModel} from "../../../infrastructure/models/link/base-link-model";
import {CompetitorStandingsCompetitionViewModel} from "../../competitor-standings-widget/models/competitor-standings-competition.view-model";
import {StandingsDataSource} from "../../../domain/standings/models/standings-data-source";
import {StandingsCompetitionRulesViewModel} from "../../../domain/standings/models/competition-rules/standings.competition-rules.view-model";
import {ENTITY_SCORES} from "../../entity-scores-widget/entity-scores-widget.consts";
import {StandingsSeasonsFilterViewModel} from "../../../domain/standings/models/seasons-filter/standings.seasons-filter.view-model";
import {StandingsSeasonsFilterDomainModel} from "../../../domain/standings/models/seasons-filter/standings.seasons-filter.domain-model";
import {ENTITY_DESCRIPTION} from "../../../modules/entity-description-module/entity-description.module.consts";
import {EXTERNAL_AD_CONFIG} from "../../../apps/website/consts/external-ad-config";
import {CompetitorStandingsPageLinkModel} from "../../../containers/competitor-container/pages/standings-page/models/competitor.standings-page.link-model";
import {NEW_COMPETITOR_CONTAINER} from "../../../containers/competitor-container/competitor-container.consts";

@makeObservableClass
export class StandingsWidgetState extends BaseModuleState {
  dataSource: StandingsDataSource;
  @observable currentCompetitionDataSource: StandingsDataSource;
  @observable collapsedCompetitorIds: Array<Number>;
  @observable highlightedCompetitors: Array<Number> = [];
  @observable hoveredCompetitorIds: Array<Number> = [];
  @observable selectedTabIndex: Number;
  @observable isPreviewStandings: Boolean;
  @observable competitorId: Number;
  @observable _isSmallView: Boolean;
  terms: StandingsWidgetTerms;
  @observable currentStageNum: Number;
  @observable lastSelectedStandingsCompetition: Number;
  @observable selectedCompetitionId: Number;
  @observable selectedSeasonNum: Number;

  static injectables = [...BaseMobxState.injectables, NavigationManager, AppStore, LoggerUtils, DatetimeUtils, DeviceViewModel, TermsUtils, StandingsWidgetConfig, CompetitionFactory];

  @computed get isLoadingWidget(): Boolean {
    const isLoadingWidget: Boolean = this.fetchingState === FETCHING_STATES.fetching || this.componentState === COMPONENT_STATES.loading

    return isLoadingWidget;
  }

  @computed get currentDataSource(): StandingsDataSource {
    const currentDataSource: StandingsDataSource = this.currentCompetitionDataSource || this.dataSource;

    return currentDataSource;
  }

  @computed get seasonsFilter(): Array<StandingsSeasonsFilterViewModel> {
    return this.currentDataSource?.seasonsFilter?.map((season: StandingsSeasonsFilterDomainModel) => {
      return new StandingsSeasonsFilterViewModel(season)
    });
  }

  @computed get currentFilterSeasonNum(): Array<StandingsSeasonsFilterViewModel> {
    const seasonNum: Number = this.selectedSeasonNum || this.dataSource?.standings?.first()?.seasonNum;
    const currentFilterSeasonNum: Number = this.currentDataSource?.seasonsFilter?.find((season: StandingsSeasonsFilterDomainModel) => seasonNum === season.seasonNum)?.seasonNum

    return currentFilterSeasonNum;
  }

  @computed get noHoverCompetitorsIds(): Boolean {
    return this.hoveredCompetitorIds.length === 0;
  }

  get onlyMajorColumns(): Boolean {
    return this.appSettings.bootstrapSettings.standingsOnlyMajorColumns;
  }

  @computed get standings(): StandingsViewModel {
    const standings: StandingsViewModel = this.currentDataSource?.standings?.length > 0 && new StandingsViewModel(this.currentDataSource?.standings?.first(), this.onlyMajorColumns);

    return standings;
  }

  @computed get groups(): Array<StandingsWidgetRowViewModel> {
    const groups: Array<StandingsWidgetRowViewModel> = this.isPreviewStandings ? this.previewStandingGroups : this.rowsByGroup;

    return groups;
  }

  @computed get rowsByGroup(): Array<StandingsWidgetRowViewModel> {
    let groups: Array<StandingsWidgetGroupedRowsViewModel> = [];

    this.rows?.forEach((row: StandingsWidgetRowViewModel) => {
      const existingGroup = groups.find((groupedRow: StandingsWidgetGroupedRowsViewModel) => groupedRow.num === row.group.num);

      if (existingGroup) {
        existingGroup.rows.push(row);
      } else {
        const newGroup = new StandingsWidgetGroupedRowsViewModel(row.group, [row], this.competitorId, this.competition?.hasStandingsGroups, this.hasGroups);
        groups.push(newGroup);
      }
    });

    if (this.hasGroups) {
      groups = groups.filter((row: StandingsWidgetGroupedRowsViewModel) => row.num !== -1);
    }

    return groups;
  }

  @computed get competition(): CompetitionViewModel {
    const competition = this.selectedCompetitionId > 0 ? this.dataSource?.competitions?.find(comp => comp.id === this.selectedCompetitionId) : this.dataSource?.competitions?.first();

    return competition && new CompetitionViewModel(competition);
  }

  get winnerDescription(): String {
    return this.standings?.winnerDescription;
  }

  get collapsedTitle(): String {
    return this.isCollapsed || this.props.source === APP.ANALYTICS.SOURCES.standingsPreview ? this.terms.GAME_CENTER_STANDING_POSITION : null;
  }

  get headers(): Array<StandingsHeaderViewModel> {
    let headers = [];

    this.standings?.headers?.forEach(header => headers.push(header));

    return headers;
  }

  get hasNotMajorHeaders(): Boolean {
    return !!this.headers?.find((header: StandingsHeaderViewModel) => !header.isMajor)
  }

  get hasLiveGame(): Boolean {
    return this.currentDataSource?.games?.length > 0
  }

  @computed get rows(): Array<StandingsWidgetRowViewModel> {
    const rows: Array<StandingsWidgetRowViewModel> = this.standings?.rows?.map((row: StandingsRowDomainModel) => {
      const competitor: CompetitorViewModel = new StandingWidgetCompetitorViewModel(row.competitor);
      const isHighlighted: Boolean = this.highlightedCompetitors.includes(row.competitor.id)
      const previousPositions: String = this.terms.STANDINGS_PREVIOUS_POSITIONS;
      const winnerTitle: String = this.terms.WEB_STANDINGS_WON_TITLE;
      const rowVM: StandingsWidgetRowViewModel = new StandingsWidgetRowViewModel(row, competitor, this.headers, this.datetimeUtils, isHighlighted, this.device.isRtl, previousPositions, winnerTitle);

      return rowVM;
    });

    const filteredRows: Array<StandingsWidgetRowViewModel> = !this.collapsedCompetitorIds.length ? rows : rows?.filter((row: StandingsWidgetRowViewModel) => this.collapsedCompetitorIds.includes(row.competitor.id));

    return filteredRows?.sort((a, b) => a.position - b.position);
  }

  @computed get standingsRows(): Array<StandingsWidgetRowViewModel> {
    return this.isPreviewStandings ? this.groups?.first()?.previewStandingsRows : this.groups?.first()?.rows;
  }

  @computed get previewStandingGroups(): Array<StandingsWidgetGroupedRowsViewModel> {
    const previewStandingGroups: Array<StandingsWidgetGroupedRowsViewModel> = this.competitorId ? this.competitorGroups : this.rowsByGroup.slice(0, 2);

    return previewStandingGroups
  }

  @computed get competitorGroups(): Array<StandingsWidgetGroupedRowsViewModel> {
    const competitorNumGroup: Number = this.rows?.filter(row => row.competitor.id === this.competitorId)?.first()?.group.num || 1;

    const competitorGroups: Array<StandingsWidgetGroupedRowsViewModel> = this.rowsByGroup.filter((group: StandingsWidgetGroupedRowsViewModel) => [competitorNumGroup, competitorNumGroup + 1].includes(group.num));
    const isGroupMaxRows = competitorGroups?.first()?.rows.length > STANDINGS_PREVIEW_WIDGET.ROWS.maxStandingsRows

    return isGroupMaxRows ? [competitorGroups.first()] : competitorGroups
  }

  get footerLinkModel(): BaseLinkModel {
    return this.standings && this.competitorId && !this.isTennis ? this.competitorFooterLinkModel : this.competitionFooterLinkModel;
  }

  get isTennis(): Boolean {
    return this.competition.sport.id === SPORT_TYPES.tennis;
  }

  get competitionFooterLinkModel(): CompetitorStandingsPageLinkModel {
    return this.standings.competition.standingsLinkModel;
  }

  get competitorFooterLinkModel(): CompetitorStandingsPageLinkModel {
    return this.competitor ? new CompetitorStandingsPageLinkModel(this.competitorId, this.competitor.sport.nameForURL, this.competitor.nameForURL, NEW_COMPETITOR_CONTAINER.FILTERS.standings, null, this.competitor.isFollow) : null;
  }

  @computed get footerRouteProps() {
    const highlightedCompetitors: Array<Number> = this.collapsedCompetitorIds.map((id: Number) => id);
    const dataSource = {
      games: this.currentDataSource?.games,
      standings: this.currentDataSource?.standings,
      competitions: this.currentDataSource.competitions
    }

    const footerRouteProps = {dataSource, highlightedCompetitors, selectedCompetitionId: this.selectedCompetitionId}

    return footerRouteProps
  }

  get destinations(): Array<StandingsDestinationViewModel> {
    const destinations: Array<StandingsDestinationViewModel> = this.standings?.destinations;

    return destinations;
  }

  @computed get hasGroups(): Boolean {
    const hasGroups = this.standings?.groups?.length > 1 && this.standings?.rows?.filter(row => row.group.num > 0).length > 0;

    return hasGroups;
  }

  @computed get isCollapsed(): Boolean {
    const isCollapsed = this.collapsedCompetitorIds?.length > 0;

    return isCollapsed;
  }

  @computed get hasFilters(): Boolean {
    const hasFilters = this.filters?.length > 0;

    return hasFilters;
  }

  @computed get activeFilter(): Number {
    const activeFilter: Number = this.hasFilters ? this.tabs.find(t => t.active)?.type : null;

    return activeFilter;
  }

  @computed get isGroupCategory(): Boolean {
    const isGroupCategory: Boolean = this.standings?.currentGroupCategory > 0;

    return isGroupCategory;
  }

  @computed get filters(): Array<StandingsWidgetFilterViewModel> {
    const entities = this.isGroupCategory ? this.standings?.groupCategories : this.standings?.filterTypes;
    const filters: Array<StandingsWidgetFilterViewModel> = entities?.map(entity => new this.tableType(entity))

    return filters
  }

  @computed get tableType(): StandingsWidgetFilterViewModel {
    const tableType: StandingsWidgetFilterViewModel = this.isGroupCategory ? StandingsWidgetFilterGroupCategoryViewModel : StandingsWidgetFilterTableTypesViewModel;

    return tableType;
  }

  @computed get tabs(): Array<StandingWidgetTabViewModel> {
    const tabs: Array<StandingWidgetTabViewModel> = this.filters?.map((filter: StandingsWidgetFilterViewModel, index: Number) => {
      const isActive = index === this.selectedTabIndex;
      const tab: StandingWidgetTabViewModel = new StandingWidgetTabViewModel(index, filter.type, filter.name, isActive);

      return tab;
    });

    return tabs;
  }

  @computed get shouldRenderTabs(): Boolean {
    const shouldRenderTabs: Boolean = !this.isCollapsed && this.hasFilters && !this.isPreviewStandings && !this.appSettings.hideHomeAwayTabs;

    return shouldRenderTabs;
  }

  @override get isPopulated() {
    return this.componentState === COMPONENT_STATES.loading || this.rows?.length > 0;
  }

  @computed get isLive(): Boolean {
    return this.competition?.hasLiveStandings;
  }

  @computed get shouldRenderLegendButton(): Boolean {
    const shouldRenderLegendButton: Boolean = !this.isCollapsed && this.device.isSmallView && this.destinations?.length > 0 && !this.isPreviewStandings;

    return shouldRenderLegendButton
  }

  @computed get shouldRenderLegendContent(): Boolean {
    const shouldRenderLegendContent: Boolean = !this.isCollapsed && this.destinations?.length > 0 && !this.isPreviewStandings;

    return shouldRenderLegendContent
  }

  @computed get shouldRenderLegendFooter(): Boolean {
    const shouldRenderLegendFooter: Boolean = !this.isCollapsed && !this.isPreviewStandings && !this.device.isSmallView

    return shouldRenderLegendFooter
  }

  @computed get shouldRenderFooterButton(): Boolean {
    const shouldRenderFooterButton: Boolean = this.isCollapsed || this.isPreviewStandings

    return shouldRenderFooterButton
  }

  @computed get footerLabel(): String {
    const standingsFooterLabel = this.isCompetitorStandings && !this.isTennis ? this.competitorFooterLabel : this.competitionFooterLabel;

    return standingsFooterLabel
  }

  @computed get competitionFooterLabel(): String {
    const standingsLabel: String = this.isTennis ? this.terms.WEB_GAME_COMPETITION_RANKING_BUTTON : (this.competition?.hasStandingsGroups ? this.terms.WEB_GAME_COMPETITION_GROUPS_BUTTON : this.terms.WEB_GAME_COMPETITION_STANDINGS_BUTTON);
    const competitionFooterLabel: String = standingsLabel.format({
      "COMPETITION_NAME": this.isTennis ? this.competition.name : (this.competition.shortName || this.competition.name),
    });
    return competitionFooterLabel;
  }

  @computed get competitorFooterLabel(): String {
    if (!this.competitor) {
      return '';
    }
    const standingsLabel: String = this.competition?.hasStandingsGroups ? this.terms.WEB_SEE_COMPETITOR_GROUPS : this.terms.WEB_SEE_COMPETITOR_STANDINGS;
    const competitorFooterLabel: String = standingsLabel.format({
      "COMPETITOR_NAME": this.competitor.shortName || this.competitor.name,
    });
    return competitorFooterLabel;

  }

  @computed get isSmallView(): Boolean {
    return this._isSmallView || this.device.isSmallView;
  }

  @computed get shouldRenderGroupArrows(): Boolean {
    const shouldRenderGroupArrows: Boolean = !this.appSettings.useScrollingButtons && !this.appSettings.bootstrapSettings.limitHeightDisplay

    return shouldRenderGroupArrows;
  }

  get entityType(): Number {
    const entityType = APP.ANALYTICS.ENTITY_DISPLAY_TYPES[this.competitorId ? CompetitorViewModel.name : CompetitionViewModel.name];

    return entityType;
  }

  get poweredByTerm(): String {
    return this.terms.WIDGETS_STANDINGS_POWERED_BY_TEMPLATE;
  }

  @computed get stagesWithStandings(): Array<StandingsWidgetStageViewModel> {
    const isCurrentSeason: Boolean = !this.selectedSeasonNum || this.selectedSeasonNum === this.competition?.currentSeasonNum;
    let stagesWithStandings: Array<StandingsWidgetStageViewModel> = isCurrentSeason ? this.competition?.currentSeason?.stages?.filter((stage: SeasonStageDomainModel) => stage.hasStandings)?.map((stage: SeasonStageDomainModel) => new StandingsWidgetStageViewModel(stage, this.competition)) : null;

    return stagesWithStandings;
  }

  @computed get currentStage(): StandingsWidgetStageViewModel {
    return this.stagesWithStandings?.find((stage: StandingsWidgetStageViewModel) => stage.num === (this.currentStageNum > 0 ? this.currentStageNum : stage.currentCompetitionStageNum));
  }

  @computed get stageNum(): Number {
    return this.props.stageNum || this.currentStage?.num
  }

  @computed get seasonNum(): Number {
    return this.props.seasonNum || this.currentStage?.competition?.currentSeasonNum
  }

  @computed get shouldRenderStagesHorizontalMenu(): Boolean {
    return !this.isCollapsed && this.stagesWithStandings?.length > 1 && !this.isPreviewStandings;
  }

  @computed get isStandingsPage(): Boolean {
    return !(this.isPreviewStandings || this.isCollapsed);
  }

  @computed get isCompetitorStandings(): Boolean {
    return !!this.competitorId;
  }

  @computed get competitor(): CompetitorDomainModel {
    const competitor: CompetitorDomainModel = this.standings?.rows?.find((standing: StandingsRowDomainModel) => standing.competitor?.id === this.competitorId)?.competitor;

    return competitor;
  }

  @computed get competitions(): Array<CompetitorStandingsCompetitionViewModel> {
    const filteredCompetitions: Array<CompetitionDomainModel> = this.dataSource?.competitions?.filter(competition => competition.hasStandings || competition.hasStageWithStandings);
    return filteredCompetitions?.map(((competitionDomain: CompetitionDomainModel) => {
      const isSelected: Boolean = competitionDomain.id === this.selectedCompetitionId;

      return new CompetitorStandingsCompetitionViewModel(competitionDomain, isSelected, this.terms)
    }));
  }

  @computed get shouldRenderDropdown(): Boolean {
    let shouldRenderDropdown: Boolean = false;

    if (!this.isWidget) {
      if (this.props.context === ENTITY_SCORES.TYPES.competitor) {
        shouldRenderDropdown = this.competitions?.length > 0 && !this.appSettings.hideCompetitionTitle;
      } else if (this.props.context === ENTITY_SCORES.TYPES.competition) {
        shouldRenderDropdown = this.seasonsFilter?.length > 1;
      }
    }

    return shouldRenderDropdown;
  }

  @computed get shouldRenderCompetitionRules(): Boolean {
    const shouldRenderCompetitionRules: Boolean = !this.isPreviewStandings && this.standings?.competitionRules && !this.isCollapsed;

    return shouldRenderCompetitionRules;
  }

  get competitionRules(): StandingsCompetitionRulesViewModel {
    const competitionRules: StandingsCompetitionRulesViewModel = this.standings?.competitionRules;

    return competitionRules;
  }

  @computed get tieBreakTitle(): String {
    const tieBreakTitle: Boolean = this.terms.WEB_STANDING_TIEBREAKERS_CARD_HEADER.format({
      "COMPETITION_NAME": this.competition.shortName || this.competition.name
    })

    return tieBreakTitle;
  }

  @computed get userCountryId(): Number {
    return this.appState.locale?.userCountryId;
  }

  @computed get dbaWebPageContext(): DBA.PAGE {
    let dbaWebPageContext = null;
    if (this.isStandingsPage) {
      if (this.isCompetitorStandings) {
        dbaWebPageContext = DBA.PAGE.competitor;
      } else
        dbaWebPageContext = DBA.PAGE.competition;
    }
    return dbaWebPageContext;
  }

  @computed get isWidget(): Boolean {
    return this.appState.settings.isWidget || this.appState.isBettingPage;
  }

  @computed get sportTypeId(): Number {
    return this.appState.context.sportType?.id;
  }

  @computed get isCompetitorPage(): Boolean {
    const isCompetitorPage: Boolean = this.currentLinkModel?.name === ROUTE_NAMES.competitorScores;

    return isCompetitorPage;
  }

  @computed get entityDescriptionId(): Number {
    return this.isCompetitorPage ? this.competitorId : this.selectedCompetitionId
  }

  @computed get entityDescriptionType(): Number {
    return this.isCompetitorPage ? ENTITY_DESCRIPTION.ENTITY_TYPE.competitor : ENTITY_DESCRIPTION.ENTITY_TYPE.competition
  }

  @computed get shouldRenderEntityDescription(): Boolean {
    const shouldRenderEntityDescription: Boolean = this.isCompetitorPage ?
      this.appState.entityDescriptionModel?.isValid(null, null, this.entityDescriptionId, this.appState.language?.id, this.appState.locale?.userCountryId, ENTITY_DESCRIPTION.ENTITY_TYPE.competitor) :
      this.appState.entityDescriptionModel?.isValid(this.sportTypeId, this.entityDescriptionId, null, this.appState.language?.id, this.appState.locale?.userCountryId, ENTITY_DESCRIPTION.ENTITY_TYPE.competition)

    return shouldRenderEntityDescription
  }

  @computed get pointsDeductions() {
    return this.standings.pointsDeductions;
  }

  @computed get pointsDeductionsTitle() {
    return this.terms.WEB_POINTS_CORRECTION.format({"COMPETITION_NAME": this.competition?.longName || this.competition?.name})
  }

  @computed get shouldRenderExternalAd() {
    return this.isStandingsPage && !this.isWidget && this.isSmallView;
  }

  @computed get externalAdConfig() {
    return this.isCompetitorPage ? EXTERNAL_AD_CONFIG.MOBILE.COMPETITOR.AD320X250_MID1_1_STANDINGS : EXTERNAL_AD_CONFIG.MOBILE.COMPETITION.AD300X250_MID1_STANDINGS;
  }
}
